import { useContext, useEffect, useMemo, useState } from "react";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import { ExpandLessOutlined, ExpandMoreOutlined } from "@mui/icons-material";
import { Box, Collapse, Grid, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import { AppContext } from "App";
import { FinancialIcon } from "components/Icons/Icons";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { LinearIndicator } from "mui-toolkit/LinearIndicator";
import { ValueLabel } from "mui-toolkit/ValueLabel";
import { useSnackbar } from "notistack";
import { formatFixed2, formatPercentage, formatPrice } from "utility/formatters";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import KPICalculationTable from "./KPICalculationTable";
import useFinanceReport from "./useFinanceReport";

export default function FinancialScorecardWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { addAttachments } = useChatAssistantAttachments();

  const isXL = useMediaQuery((theme) => theme.breakpoints.up("xl"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();

  const { component: confirmDialogComponent } = useDialog();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);

  // State for managing which KPIs are expanded
  const [expandedKPIs, setExpandedKPIs] = useState({});

  const toggleKPIExpansion = (kpiName) => {
    setExpandedKPIs((prev) => ({
      ...prev,
      [kpiName]: !prev[kpiName],
    }));
  };

  // Component to wrap LinearIndicator with calculation details
  const LinearIndicatorWithCalculation = ({ title, calculation, ...props }) => {
    const isExpanded = expandedKPIs[title] || false;

    // Check if we're in print mode
    const isPrinting = useMediaQuery("print");

    return (
      <Box className="pageBreakAvoid">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Typography variant="body1" component="div" sx={{ fontWeight: 500, fontSize: "1.1rem" }}>
            {title}
          </Typography>
          {calculation && (
            <IconButton
              onClick={() => toggleKPIExpansion(title)}
              size="small"
              sx={{
                padding: "4px",
                "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
              }}
              aria-expanded={isExpanded}
              aria-label="show calculation"
            >
              {isExpanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
            </IconButton>
          )}
        </Box>
        <LinearIndicator
          {...props}
          title="" // Remove title from LinearIndicator since we're showing it above
        />
        {calculation && (
          <Collapse in={isExpanded || isPrinting} timeout="auto" unmountOnExit={!isPrinting}>
            <Box sx={{ width: "100%" }}>
              <KPICalculationTable calculation={calculation} fullWidth={true} />
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear, globalDispatch]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const {
    isLoading,
    isLoadingBalanceSheet,
    balanceSheet,
    balanceSheetPriorYear,
    isLoadingRatios,
    ratios,
    incomeStatement,
    budget,
    arcPlc,
    getKPICalculations,
  } = useFinanceReport({
    onError: handleError,
    currentYear,
  });

  const kpiCalculations = useMemo(() => {
    return getKPICalculations();
  }, [balanceSheet, balanceSheetPriorYear, ratios, incomeStatement, budget, arcPlc]);

  // Add attachment when data is loaded
  useEffect(() => {
    if (kpiCalculations && !isLoading && !isLoadingRatios && !isLoadingBalanceSheet) {
      addAttachments([
        {
          name: "Financial Scorecard",
          content: kpiCalculations,
        },
      ]);
    }
  }, [kpiCalculations, isLoading, isLoadingRatios, isLoadingBalanceSheet, addAttachments]);

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title="Financial Score Card"
        category={{
          title: "Financial Analysis",
          icon: <FinancialIcon />,
        }}
      />

      <WorkspacePageHeader title="Year" currentYear={currentYear} onYearChange={setCurrentYear} isLoading={isLoading} />

      <Grid
        container
        columnSpacing={10}
        sx={{
          "@media print": {
            backgroundColor: "transparent",
            margin: 0,
            padding: 0,
            width: "100%",
            "& .MuiCollapse-root": {
              height: "auto !important",
              visibility: "visible !important",
              minHeight: "auto !important",
              "& .MuiCollapse-wrapper": {
                height: "auto !important",
                minHeight: "auto !important",
              },
              "& .MuiCollapse-wrapperInner": {
                height: "auto !important",
                minHeight: "auto !important",
              },
              "& .MuiTableContainer-root": {
                fontSize: "0.7rem",
                "& .MuiTableCell-root": {
                  padding: "2px 4px",
                  fontSize: "0.7rem",
                },
              },
              "& .MuiTypography-caption": {
                fontSize: "0.6rem",
                lineHeight: "1.1",
              },
              margin: "2px 0",
              pageBreakInside: "auto",
            },
            "& .MuiIconButton-root": {
              display: "none !important",
            },
            "& > .MuiGrid-item": {
              flexBasis: "100% !important",
              maxWidth: "100% !important",
              width: "100%",
              pageBreakInside: "auto",
              breakInside: "auto",
              margin: "0 0 4px 0",
              padding: "2px 0",
              boxSizing: "border-box",
            },
            "& .linearIndicator": {
              pageBreakInside: "auto",
              width: "100%",
              "& .MuiGrid-container": {
                margin: 0,
                width: "100%",
                "& .MuiGrid-item": {
                  padding: "1px",
                },
              },
              "& .MuiTypography-root": {
                fontSize: "0.7rem",
                lineHeight: "1.1",
              },
              "& .indicator": {
                height: "6px",
              },
            },
          },
        }}
      >
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{ borderBottom: "1px solid", borderColor: "colors.borderLight", pb: 2, mb: 2 }}
          className="pageBreakAfter"
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Liquidity
          </Typography>

          <Stack direction="column" spacing={5}>
            <ValueLabel
              label="Working Capital"
              value={formatPrice(balanceSheet?.working_capital)}
              isLoading={isLoadingBalanceSheet}
            />

            <LinearIndicatorWithCalculation
              title="Current Ratio"
              calculation={kpiCalculations?.currentRatio}
              checkpoint1={1.3}
              checkpoint2={2.0}
              value={balanceSheet?.current_ratio}
              isLoading={isLoadingBalanceSheet}
              valueFormatter={formatFixed2}
            />

            <LinearIndicatorWithCalculation
              title="Working Capital to Gross Revenue"
              calculation={kpiCalculations?.workingCapitalToGrossRevenue}
              checkpoint1={0.1}
              checkpoint2={0.3}
              value={ratios?.working_capital_to_gross_revenue_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />
          </Stack>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{ borderBottom: "1px solid", borderColor: "colors.borderLight", pb: 2, mb: 2 }}
          className="pageBreakAfter"
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Solvency
          </Typography>

          <Stack direction="column" spacing={5}>
            <LinearIndicatorWithCalculation
              title={"Debt-to-Assets" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.debtToAssets}
              checkpoint1={0.6}
              checkpoint2={0.3}
              value={balanceSheet?.debt_to_assets_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingBalanceSheet}
            />

            <LinearIndicatorWithCalculation
              title={"Equity-to-Assets" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.equityToAssets}
              checkpoint1={0.4}
              checkpoint2={0.7}
              value={balanceSheet?.equity_to_assets_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoading}
            />
          </Stack>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{ borderBottom: "1px solid", borderColor: "colors.borderLight", pb: 2, mb: 2 }}
          className="pageBreakAfter"
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Profitability
          </Typography>

          <Stack direction="column" spacing={5}>
            <Box className="flexWrapContainer">
              <ValueLabel
                label="Net Income (Pre-Tax)"
                value={formatPrice(ratios?.net_income_pre_tax)}
                isLoading={isLoadingRatios}
              />
              <ValueLabel label="EBIDTA" value={formatPrice(ratios?.ebitda)} isLoading={isLoadingRatios} />
            </Box>

            <LinearIndicatorWithCalculation
              title="Return on Assets"
              calculation={kpiCalculations?.returnOnAssets}
              checkpoint1={0.04}
              checkpoint2={0.08}
              value={ratios?.return_on_assets_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />

            <LinearIndicatorWithCalculation
              title="Return on Equity"
              calculation={kpiCalculations?.returnOnEquity}
              checkpoint1={0.03}
              checkpoint2={0.1}
              value={ratios?.return_on_equity_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />

            <LinearIndicatorWithCalculation
              title="Operating Profit Margin"
              calculation={kpiCalculations?.operatingProfitMargin}
              checkpoint1={0.15}
              checkpoint2={0.25}
              value={ratios?.operating_profit_margin_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />
          </Stack>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{ borderBottom: "1px solid", borderColor: "colors.borderLight", pb: 2, mb: 2 }}
          className="pageBreakAfter"
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Debt Repayment Capacity
          </Typography>

          <Stack direction="column" spacing={5}>
            <Box className="flexWrapContainer">
              <ValueLabel
                label="TD Repayment Capacity"
                value={formatPrice(ratios?.debt_repayment_capacity)}
                isLoading={isLoadingRatios}
              />
              <ValueLabel
                label="Repayment Margin"
                value={formatPrice(ratios?.repayment_margin)}
                isLoading={isLoadingRatios}
              />
              <ValueLabel
                label="Replacement Margin"
                value={formatPrice(ratios?.replacement_margin)}
                isLoading={isLoadingRatios}
              />
            </Box>

            <LinearIndicatorWithCalculation
              title={"Term-Debt Coverage" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.termDebtCoverage}
              checkpoint1={1.25}
              checkpoint2={1.75}
              value={ratios?.term_debt_coverage_ratio}
              valueFormatter={formatFixed2}
              isLoading={isLoadingRatios}
            />

            <LinearIndicatorWithCalculation
              title={"Replacement Coverage" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.replacementMarginCoverage}
              checkpoint1={1.1}
              checkpoint2={1.5}
              value={ratios?.replacement_coverage_ratio}
              valueFormatter={formatFixed2}
              isLoading={isLoadingRatios}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md={6} sx={{ borderBottom: "1px solid", borderColor: "colors.borderLight", pb: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Financial Efficiency
          </Typography>

          <Stack direction="column" spacing={5}>
            <LinearIndicatorWithCalculation
              title="Asset Turnover Ratio"
              calculation={kpiCalculations?.assetTurnover}
              checkpoint1={0.3}
              checkpoint2={0.45}
              value={ratios?.asset_turnover_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />

            <LinearIndicatorWithCalculation
              title={"Operating/Expense" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.operatingExpenseRatio}
              checkpoint1={0.8}
              checkpoint2={0.6}
              value={ratios?.operating_expense_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />

            <LinearIndicatorWithCalculation
              title={"Depreciation/Expense" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.depreciationExpenseRatio}
              checkpoint1={0.1}
              checkpoint2={0.05}
              value={ratios?.depreciation_expense_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />

            <LinearIndicatorWithCalculation
              title={"Interest/Expenses" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.interestExpenseRatio}
              checkpoint1={0.1}
              checkpoint2={0.05}
              value={ratios?.interest_expense_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />

            <LinearIndicatorWithCalculation
              title={"Net Income" + (!isXL ? "" : " Ratio")}
              calculation={kpiCalculations?.netIncomeRatio}
              checkpoint1={0.1}
              checkpoint2={0.2}
              value={ratios?.income_ratio}
              valueFormatter={formatPercentage}
              isLoading={isLoadingRatios}
            />
          </Stack>
        </Grid>
      </Grid>

      <WorkspaceActionBar content={<></>} />

      {confirmDialogComponent}
    </>
  );
}
